import { InvaderKind } from "./types";

export type Sprite = number[][];

// Sprites are pure data drawn many times per frame at a handful of
// scale/colour combinations, so each combination is rasterised once into an
// offscreen canvas and blitted thereafter. The hero draws ~145 invaders a
// frame; filling every cell individually would be thousands of calls.
const cache = new Map<string, HTMLCanvasElement>();
const spriteIds = new WeakMap<Sprite, number>();
let nextSpriteId = 0;

function keyFor(sprite: Sprite, scale: number, color: string): string {
  let id = spriteIds.get(sprite);
  if (id === undefined) {
    id = nextSpriteId++;
    spriteIds.set(sprite, id);
  }
  return id + ":" + scale + ":" + color;
}

function rasterise(sprite: Sprite, scale: number, color: string): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, sprite[0].length * scale);
  canvas.height = Math.max(1, sprite.length * scale);
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.fillStyle = color;
    for (let row = 0; row < sprite.length; row++) {
      for (let col = 0; col < sprite[row].length; col++) {
        if (sprite[row][col]) {
          ctx.fillRect(col * scale, row * scale, scale, scale);
        }
      }
    }
  }
  return canvas;
}

/** Draws a 0/1 sprite grid as filled `scale`x`scale` pixels in `color`. */
export function drawSprite(
  ctx: CanvasRenderingContext2D,
  sprite: Sprite,
  x: number,
  y: number,
  scale: number,
  color: string
): void {
  const key = keyFor(sprite, scale, color);
  let rendered = cache.get(key);
  if (!rendered) {
    rendered = rasterise(sprite, scale, color);
    cache.set(key, rendered);
  }
  // Whole-pixel destinations keep the pixel art from being resampled.
  ctx.drawImage(rendered, Math.round(x), Math.round(y));
}

/** Test seam: drops every rasterised sprite. */
export function clearSpriteCache(): void {
  cache.clear();
}

export const PLAYER_SPRITE: Sprite = [
  [0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 1, 1, 1, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 1, 1, 1, 0, 0, 0, 0, 0],
  [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
];

const TOP_A: Sprite = [
  [0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0],
  [0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0],
  [0, 0, 1, 1, 1, 1, 1, 1, 1, 0, 0],
  [0, 1, 1, 0, 1, 1, 1, 0, 1, 1, 0],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  [1, 0, 1, 1, 1, 1, 1, 1, 1, 0, 1],
  [1, 0, 1, 0, 0, 0, 0, 0, 1, 0, 1],
  [0, 0, 0, 1, 1, 0, 1, 1, 0, 0, 0],
];

const TOP_B: Sprite = [
  [0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0],
  [1, 0, 0, 1, 0, 0, 0, 1, 0, 0, 1],
  [1, 0, 1, 1, 1, 1, 1, 1, 1, 0, 1],
  [1, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  [0, 0, 1, 1, 1, 1, 1, 1, 1, 0, 0],
  [0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0],
  [0, 1, 1, 0, 0, 0, 0, 0, 1, 1, 0],
];

const MID_A: Sprite = [
  [0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0],
  [0, 0, 0, 0, 1, 0, 1, 0, 0, 0, 0],
  [0, 0, 0, 1, 1, 1, 1, 1, 0, 0, 0],
  [0, 0, 1, 1, 0, 1, 0, 1, 1, 0, 0],
  [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
  [0, 1, 0, 1, 1, 1, 1, 1, 0, 1, 0],
  [0, 1, 0, 1, 0, 0, 0, 1, 0, 1, 0],
  [0, 0, 0, 0, 1, 1, 1, 0, 0, 0, 0],
];

const MID_B: Sprite = [
  [0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0],
  [0, 1, 0, 0, 1, 0, 1, 0, 0, 1, 0],
  [0, 1, 0, 1, 1, 1, 1, 1, 0, 1, 0],
  [0, 1, 1, 1, 0, 1, 0, 1, 1, 1, 0],
  [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
  [0, 0, 1, 1, 1, 1, 1, 1, 1, 0, 0],
  [0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0],
  [0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0],
];

const BOTTOM_A: Sprite = [
  [0, 0, 1, 1, 1, 1, 0, 0],
  [0, 1, 1, 1, 1, 1, 1, 0],
  [1, 1, 1, 1, 1, 1, 1, 1],
  [1, 1, 0, 1, 1, 0, 1, 1],
  [1, 1, 1, 1, 1, 1, 1, 1],
  [0, 0, 1, 0, 0, 1, 0, 0],
  [0, 1, 0, 1, 1, 0, 1, 0],
  [1, 0, 1, 0, 0, 1, 0, 1],
];

const BOTTOM_B: Sprite = [
  [0, 0, 1, 1, 1, 1, 0, 0],
  [0, 1, 1, 1, 1, 1, 1, 0],
  [1, 1, 1, 1, 1, 1, 1, 1],
  [1, 1, 0, 1, 1, 0, 1, 1],
  [1, 1, 1, 1, 1, 1, 1, 1],
  [0, 1, 0, 1, 1, 0, 1, 0],
  [1, 0, 0, 0, 0, 0, 0, 1],
  [0, 1, 0, 0, 0, 0, 1, 0],
];

/** Two animation frames per invader kind. */
export const INVADER_SPRITES: Record<InvaderKind, [Sprite, Sprite]> = {
  top: [TOP_A, TOP_B],
  mid: [MID_A, MID_B],
  bottom: [BOTTOM_A, BOTTOM_B],
};
